import { useEffect, useRef, useState } from 'react';
import { Calendar, Check, ChevronDown } from 'lucide-react';
import type { OptimizationReportSummary } from '@monitoring-panel/types';
import { cn } from '@/lib/utils';
import { fullDate } from './format';

interface ReportDatePickerProps {
  reports: OptimizationReportSummary[];
  selectedDate: string;
  onSelect: (reportDate: string) => void;
}

/** Dropdown of the saved reports of an execution, newest first. */
export function ReportDatePicker({ reports, selectedDate, onSelect }: ReportDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (reportDate: string) => {
    setIsOpen(false);
    if (reportDate !== selectedDate) onSelect(reportDate);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="adv-btn adv-btn--secondary adv-btn--sm"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Calendar size={14} />
        <span className="adv-num">{fullDate(selectedDate)}</span>
        {reports[0]?.reportDate === selectedDate && (
          <span className="text-adv-tertiary font-normal">· último</span>
        )}
        <ChevronDown size={14} className={cn('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="adv-dropdown absolute right-0 top-full mt-1 w-72 z-50 max-h-80 overflow-y-auto">
          {reports.map((report) => {
            const isActive = report.reportDate === selectedDate;
            return (
              <button
                key={report.reportDate}
                type="button"
                onClick={() => handleSelect(report.reportDate)}
                className={cn(
                  'adv-dropdown__item justify-between',
                  isActive && 'adv-dropdown__item--active'
                )}
              >
                <span className="flex flex-col items-start">
                  <span className="adv-num">{fullDate(report.reportDate)}</span>
                  <span className="text-adv-body-s text-adv-tertiary">
                    {report.authorName ?? report.authorEmail} ·{' '}
                    <span className="adv-num">
                      {report.applied}/{report.suggestions}
                    </span>{' '}
                    aplicadas
                    {report.pending > 0 && (
                      <>
                        {' · '}
                        <span className="adv-num">{report.pending}</span> pendientes
                      </>
                    )}
                  </span>
                </span>
                {isActive && <Check size={14} className="text-adv-accent flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
